import { useGetSocialOfferSuspense } from "@api";
import type { ISocialOfferWithFavoriteChallenges } from "@api";
import { Divider, useMediaQuery } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import {
  ModalSocialOfferProvider,
  StandardDrawerSocialOffer,
  SuspenseHandler,
  useModalSocialOfferContext,
} from "@styleguide";
import { useParams } from "react-router-dom";
import { SocialOffersDetailsBody } from "./body";
import { SocialOffersDetailsInfo } from "./info";
import { useSocialOfferDetails } from "./logics";
import { StyledPageView } from "./styles";

const SocialOfferDetailsLoaded = ({ getOffer }: { getOffer: () => ISocialOfferWithFavoriteChallenges }) => {
  const offer = getOffer();
  document.title = `${offer.name} - ${offer.provider?.name}`;
  const { setDrawerOffer, isDrawerOpen, innerOffer, setInnerOffer } = useSocialOfferDetails(offer);
  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.up("tablet"));
  const openModalSocialOffer = useModalSocialOfferContext();

  return (
    <>
      <SocialOffersDetailsInfo
        offer={innerOffer}
        setDrawerOffer={setDrawerOffer}
        onUpdate={(updatedOffer) => setInnerOffer(updatedOffer)}
      />
      {isTablet && <Divider orientation="vertical" />}
      <SocialOffersDetailsBody offerId={innerOffer.offerId} />
      <StandardDrawerSocialOffer
        open={isDrawerOpen}
        offer={innerOffer}
        handleRemoveOffer={() => setDrawerOffer(null)}
        onClose={() => setDrawerOffer(null)}
        onUpdateOffer={(updatedOffer) => {
          setDrawerOffer(updatedOffer || offer);
          setInnerOffer(updatedOffer || offer);
        }}
        openEditModal={(offer) => {
          openModalSocialOffer(offer, (updatedOffer) => setInnerOffer(updatedOffer));
          setDrawerOffer(null);
        }}
        anchor="left"
      />
    </>
  );
};

export const SocialOfferDetails = () => {
  const { offerId } = useParams<{ offerId: string }>();
  const getOffer = useGetSocialOfferSuspense(offerId ?? "");

  return (
    <ModalSocialOfferProvider>
      <StyledPageView>
        <SuspenseHandler slotProps={{ box: { height: "100%", width: "100%" } }}>
          <SocialOfferDetailsLoaded getOffer={getOffer} />
        </SuspenseHandler>
      </StyledPageView>
    </ModalSocialOfferProvider>
  );
};
// biome-ignore lint/style/noDefaultExport: <explanation>
export default SocialOfferDetails;
